import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
} from 'react'

import {
  applySelectionMarks,
  cellPosOf,
  CELL_SELECTOR,
  isInteractiveTarget,
} from '@/components/data-table/grid-selection-dom'
import {
  buildTsv,
  clampRange,
  collectValues,
  edgeScrollDelta,
  extendTo,
  isMultiCell,
  moveSelection,
  rangeSize,
  selectAll,
  singleCell,
  sumNumeric,
  type CellRange,
  type GridBounds,
  type GridCellValue,
} from '@/components/data-table/grid-selection'

const COPIED_FLASH_MS = 1800
const FOCUS_RETRY_FRAMES = 4
/** Sürüklerken imleç kapsayıcı kenarına bu kadar (px) yaklaşınca otomatik kaydırma başlar. */
const AUTOSCROLL_EDGE = 36
const AUTOSCROLL_MAX_STEP = 24
const PAGE_ROWS = 10
const NAV_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
])

interface UseGridSelectionOptions {
  /** Seçimin dizinlendiği kapsayıcı (kaydırılan gövde); olaylar ve işaretler burada. */
  containerRef: RefObject<HTMLElement | null>
  bounds: GridBounds
  /** Değişince seçim silinir (sayfa/sıralama/filtre/sütunlar): eski dizinler artık başka hücre. */
  resetKey: string
  /** Hücrenin ham değeri; satırlar ya da sütunlar değişince kimliği değişmeli. */
  getValue: (row: number, col: number) => GridCellValue
  /** Hücreler yeniden çizilince işaretleri yenilemek için: satırların kimliği. */
  rowsVersion: unknown
  /**
   * Sanal kip: satır dizini (mutlak) çizilmemişse görünür alana kaydırır; yoksa klavye hareketi
   * hedef hücreyi bulamaz. Verilirse hücre bir sonraki karelerde aranır.
   */
  scrollToRow?: (row: number) => void
}

export interface GridSelectionSummary {
  cells: number
  /** Sayısal hücre yoksa `null`. */
  sum: number | null
}

/**
 * Hücre aralığı seçimi + Ctrl+C. Durum (aralık) burada, görünüm hücre özniteliklerinde
 * (`grid-selection-dom.ts`): seçim değişince satırlar yeniden çizilmez. Mantık `grid-selection.ts`te.
 */
export function useGridSelection({
  containerRef,
  bounds,
  resetKey,
  getValue,
  rowsVersion,
  scrollToRow,
}: UseGridSelectionOptions) {
  const [range, setRange] = useState<CellRange | null>(null)
  const rangeRef = useRef<CellRange | null>(null)
  const boundsRef = useRef(bounds)
  const draggingRef = useRef(false)
  // Satıra tıklamayı bastır: tıklama önceden var olan bir seçimi bozdu ya da sürükleme bitirdi.
  const blockRowClickRef = useRef(false)
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [seenKey, setSeenKey] = useState(resetKey)
  if (seenKey !== resetKey) {
    setSeenKey(resetKey)
    setRange(null)
  }

  const commit = useCallback((next: CellRange | null) => {
    rangeRef.current = next
    setRange(next)
  }, [])
  const current = () => clampRange(rangeRef.current, boundsRef.current)

  useEffect(() => {
    boundsRef.current = bounds
    rangeRef.current = range
  }, [bounds, range])

  const active = useMemo(() => clampRange(range, bounds), [range, bounds])

  // Satırlar/sütunlar yeniden çizilince (sayfa verisi, sütun sırası) işaretler yeniden yazılır.
  const marked = useRef<{ range: CellRange | null; rows: unknown; empty: boolean }>({
    range: null,
    rows: undefined,
    empty: true,
  })
  useLayoutEffect(() => {
    const last = marked.current
    if (last.range === active && last.rows === rowsVersion) return
    if (active === null && last.empty) {
      marked.current = { range: null, rows: rowsVersion, empty: true }
      return
    }
    if (containerRef.current) applySelectionMarks(containerRef.current, active)
    marked.current = { range: active, rows: rowsVersion, empty: active === null }
  })

  // Tablonun dışına tıklanınca seçim kalkar (odak orada olmadığından Ctrl+C zaten çalışmazdı).
  const hasSelection = active !== null
  useEffect(() => {
    if (!hasSelection) return
    const onDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) commit(null)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [hasSelection, commit, containerRef])

  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  const matrix = useMemo(
    () => (active ? collectValues(active, getValue) : null),
    [active, getValue],
  )
  const summary = useMemo<GridSelectionSummary | null>(() => {
    if (!active || !matrix) return null
    const { count, sum } = sumNumeric(matrix)
    return { cells: rangeSize(active).cells, sum: count > 0 ? sum : null }
  }, [active, matrix])

  const inside = (target: EventTarget | null) =>
    target instanceof Node && containerRef.current?.contains(target) === true

  const focusCell = (row: number, col: number, attempts = 0) => {
    const cell = containerRef.current?.querySelector<HTMLElement>(
      `${CELL_SELECTOR}[data-row="${row}"][data-col="${col}"]`,
    )
    if (!cell) {
      // Sanal gövde: hücre henüz çizilmedi; kaydır, kaydırma olayı satırları çizince tekrar dene.
      if (scrollToRow && attempts < FOCUS_RETRY_FRAMES) {
        if (attempts === 0) scrollToRow(row)
        requestAnimationFrame(() => focusCell(row, col, attempts + 1))
      }
      return
    }
    // Satırlar odaklanabiliyorsa (onRowClick) Enter etkin hücrenin satırını açsın.
    const tableRow = cell.closest('tr')
    if (tableRow?.hasAttribute('tabindex')) tableRow.focus({ preventScroll: true })
    // Sabit başlık/toplam satırının altında kalmasın: kapsayıcının `scroll-padding`'i hesaba girer.
    cell.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }

  const flashCopied = () => {
    setCopied(true)
    clearTimeout(copiedTimer.current)
    copiedTimer.current = setTimeout(() => setCopied(false), COPIED_FLASH_MS)
  }

  const onMouseDown = (event: MouseEvent) => {
    if (event.button !== 0 || !inside(event.target) || isInteractiveTarget(event.target)) return
    const pos = cellPosOf(event.target)
    if (!pos) return
    const before = current()
    blockRowClickRef.current = isMultiCell(before)
    commit(event.shiftKey && before ? extendTo(before, pos) : singleCell(pos))
    draggingRef.current = true
    const stopAutoScroll = startAutoScroll()
    window.addEventListener(
      'mouseup',
      () => {
        draggingRef.current = false
        stopAutoScroll()
        if (isMultiCell(rangeRef.current)) blockRowClickRef.current = true
      },
      { once: true },
    )
    // Yerel metin seçimi/odak yerine kendi seçimimiz: odağı satıra ya da kapsayıcıya biz veririz.
    event.preventDefault()
    const tableRow = (event.target as Element).closest('tr')
    if (tableRow?.hasAttribute('tabindex')) tableRow.focus({ preventScroll: true })
    else containerRef.current?.focus({ preventScroll: true })
  }

  /**
   * Sürüklerken imleç kapsayıcının kenarına yaklaşırsa gövdeyi kaydırır ve altındaki hücreye kadar
   * seçimi uzatır (fare durağan olsa da `mouseover` gelmez, bu yüzden hücre noktadan bulunur).
   * Sabit başlık/toplam satırının altı kenar sayılır (`scroll-padding`). Dönen işlev döngüyü durdurur.
   */
  const startAutoScroll = () => {
    const pointer = { x: 0, y: 0, moved: false }
    const onMove = (event: globalThis.MouseEvent) => {
      pointer.x = event.clientX
      pointer.y = event.clientY
      pointer.moved = true
    }
    let frame = 0
    const tick = () => {
      const container = containerRef.current
      if (container && pointer.moved) {
        const rect = container.getBoundingClientRect()
        const style = getComputedStyle(container)
        const top = rect.top + (parseFloat(style.scrollPaddingTop) || 0)
        const bottom = rect.bottom - (parseFloat(style.scrollPaddingBottom) || 0)
        const dx = edgeScrollDelta(
          pointer.x,
          rect.left,
          rect.right,
          AUTOSCROLL_EDGE,
          AUTOSCROLL_MAX_STEP,
        )
        const dy = edgeScrollDelta(pointer.y, top, bottom, AUTOSCROLL_EDGE, AUTOSCROLL_MAX_STEP)
        if (dx !== 0 || dy !== 0) {
          container.scrollBy(dx, dy)
          const x = Math.min(Math.max(pointer.x, rect.left + 1), rect.right - 1)
          const y = Math.min(Math.max(pointer.y, top + 1), bottom - 1)
          const pos = cellPosOf(document.elementFromPoint(x, y))
          const before = current()
          if (before && pos && (pos.row !== before.focus.row || pos.col !== before.focus.col)) {
            commit(extendTo(before, pos))
          }
        }
      }
      frame = requestAnimationFrame(tick)
    }
    window.addEventListener('mousemove', onMove)
    frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('mousemove', onMove)
    }
  }

  const onMouseOver = (event: MouseEvent) => {
    if (!draggingRef.current) return
    const before = current()
    const pos = cellPosOf(event.target)
    if (!before || !pos || (pos.row === before.focus.row && pos.col === before.focus.col)) return
    commit(extendTo(before, pos))
  }

  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target
    if (!inside(target) || isInteractiveTarget(target)) return
    // Başlık düğmeleri ve açılır menüler dışarıda kalsın: yalnız gövde ya da kapsayıcı odakta.
    const inBody = target instanceof Element && target.closest('tbody') !== null
    if (!inBody && target !== containerRef.current) return
    const ctrl = event.ctrlKey || event.metaKey
    const before = current()

    if (ctrl && event.key.toLowerCase() === 'a') {
      const next = selectAll(boundsRef.current)
      if (!next) return
      event.preventDefault()
      commit(next)
      return
    }
    if (ctrl && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'c') {
      if (!active || !matrix) return
      // `copy` olayı çoğu tarayıcıda yalnız gerçek (metin) seçim varken ateşlenir; hücre seçiminde
      // yoktur. Bu yüzden Ctrl+C Clipboard API ile de yazılır; olay gelirse aynı metni yazar.
      void navigator.clipboard?.writeText(buildTsv(matrix)).then(flashCopied, () => undefined)
      return
    }
    if (event.key === 'Escape') {
      if (!before) return
      event.preventDefault()
      commit(null)
      return
    }

    if (!NAV_KEYS.has(event.key)) return
    if (!before) {
      // Henüz seçim yokken ilk gezinme tuşu, odaktaki satırın (yoksa ilk satırın) ilk hücresini seçer.
      const focusedRow = target instanceof Element ? target.closest('tr') : null
      const row = Number(focusedRow?.querySelector<HTMLElement>(CELL_SELECTOR)?.dataset.row ?? 0)
      const start = singleCell({ row, col: 0 })
      event.preventDefault()
      commit(start)
      focusCell(start.focus.row, start.focus.col)
      return
    }
    const next = moveSelection(
      before,
      { key: event.key, shift: event.shiftKey, ctrl },
      boundsRef.current,
      PAGE_ROWS,
    )
    if (!next) return
    event.preventDefault()
    commit(next)
    focusCell(next.focus.row, next.focus.col)
  }

  const onCopy = (event: ClipboardEvent) => {
    if (!inside(event.target) || isInteractiveTarget(event.target) || !active || !matrix) return
    event.clipboardData.setData('text/plain', buildTsv(matrix))
    event.preventDefault()
    flashCopied()
  }

  const onBlur = (event: FocusEvent) => {
    const next = event.relatedTarget
    if (next instanceof Node && !containerRef.current?.contains(next)) commit(null)
  }

  /** `onRowClick` önce sorulur: çok hücreli seçim varken ya da tıklama onu bozduysa çalışmaz. */
  const shouldBlockRowClick = useCallback(
    () => blockRowClickRef.current || isMultiCell(rangeRef.current),
    [],
  )

  return {
    summary,
    copied,
    shouldBlockRowClick,
    handlers: { onMouseDown, onMouseOver, onKeyDown, onCopy, onBlur },
  }
}
